import type { SetState } from "zustand";

import type { AskSource } from "@performyard/service-contracts";

import type { ChatStatus, ConfidenceLevel, StreamStage } from "../../types";
import type { AskPerformYardState } from "../types";

import { buildGreetingMessage, generateMessageId, getOrCreateSessionId } from "../helpers";
import { drainPendingChunks, schedulePendingChunk } from "../streaming-buffer";

export const createChatSlice = (set: SetState<AskPerformYardState>) => ({
	sessionId: getOrCreateSessionId(),
	messages: [buildGreetingMessage(false, false, false)],
	status: "idle" as ChatStatus,
	streamStage: null as StreamStage | null,
	streamMessage: null as string | null,
	error: null as string | null,
	inputValue: "",

	addUserMessage: (content: string) => {
		const id = generateMessageId();
		set((state) => ({
			messages: [
				...state.messages,
				{
					id,
					role: "user" as const,
					content,
					timestamp: new Date(),
				},
			],
			inputValue: "",
		}));
		return id;
	},

	startAssistantMessage: () => {
		const id = generateMessageId();
		set((state) => ({
			messages: [
				...state.messages,
				{
					id,
					role: "assistant" as const,
					content: "",
					isStreaming: true,
					timestamp: new Date(),
				},
			],
			status: "streaming" as const,
		}));
		return id;
	},

	appendToAssistantMessage: (messageId: string, chunk: string) => {
		schedulePendingChunk(messageId, chunk);
	},

	completeAssistantMessage: (
		messageId: string,
		sources?: AskSource[],
		confidence?: ConfidenceLevel,
		isIncomplete?: boolean
	) => {
		const remainingChunks = drainPendingChunks();

		set((state) => {
			const lastIdx = state.messages.length - 1;
			const lastMsg = state.messages[lastIdx];
			if (!lastMsg || lastMsg.id !== messageId) return state;

			const updated = state.messages.slice();
			updated[lastIdx] = {
				...lastMsg,
				content: lastMsg.content + remainingChunks,
				isStreaming: false,
				sources,
				confidence,
				isIncomplete,
			};
			return { messages: updated, status: "idle" as const, streamStage: null, streamMessage: null };
		});
	},

	setStatus: (status: ChatStatus) => set({ status }),
	setStreamStage: (stage: StreamStage | null) => set({ streamStage: stage }),
	setStreamMessage: (message: string | null) => set({ streamMessage: message }),
	setError: (error: string | null) =>
		set((state) => ({
			error,
			status: error ? "error" : state.status === "error" ? "idle" : state.status,
		})),

	updateGreetingMessage: (askHrEnabled: boolean, goalCreationEnabled: boolean, formCreationEnabled: boolean) => {
		set((state) => {
			const greetingIdx = state.messages.findIndex((m) => m.id === "greeting");
			if (greetingIdx === -1)
				return {
					messages: state.messages,
					cachedAskHrEnabled: askHrEnabled,
					cachedGoalCreationEnabled: goalCreationEnabled,
					cachedFormCreationEnabled: formCreationEnabled,
				};
			const updatedMessages = state.messages.slice();
			updatedMessages[greetingIdx] = buildGreetingMessage(askHrEnabled, goalCreationEnabled, formCreationEnabled);
			return {
				messages: updatedMessages,
				cachedAskHrEnabled: askHrEnabled,
				cachedGoalCreationEnabled: goalCreationEnabled,
				cachedFormCreationEnabled: formCreationEnabled,
			};
		});
	},
});
